import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { PresetApiContent } from "@/types/page";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// Helper function to sanitize HTML content by removing debug attributes
const sanitizeHtml = (html: string): string => {
  if (typeof html !== 'string') return html;
  return html.replace(/data-lov-id="[^"]*"|data-lov-name="[^"]*"|data-component-path="[^"]*"|data-component-line="[^"]*"|data-component-file="[^"]*"|data-component-name="[^"]*"|data-component-content="[^"]*"/g, '');
};

// Helper function to recursively sanitize object data
const sanitizeData = (data: any): any => {
  if (!data) return data;
  
  if (typeof data === 'string') {
    return sanitizeHtml(data);
  }
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }
  
  if (typeof data === 'object') {
    const result: Record<string, any> = {};
    for (const key in data) {
      result[key] = sanitizeData(data[key]);
    }
    return result;
  }
  
  return data;
};

interface PresetApiPageProps {
  content: PresetApiContent;
  isHomePage?: boolean;
}

const PRESET_API_ENDPOINTS = {
  "halacha-yomit": "https://wwosfhatkzqsgzhkigpx.supabase.co/functions/v1/halacha-yomit",
  "hayom-yom": "https://wwosfhatkzqsgzhkigpx.supabase.co/functions/v1/hayom-yom"
};

const API_TITLES = {
  "halacha-yomit": "הלכה יומית",
  "hayom-yom": "היום יום"
};

const PresetApiPage: React.FC<PresetApiPageProps> = ({ content, isHomePage = false }) => {
  const [apiData, setApiData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPresetData = async () => {
      if (!content.presetType) {
        setError("No preset type selected");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Get the API endpoint for the selected preset
        const endpoint = PRESET_API_ENDPOINTS[content.presetType];
        if (!endpoint) {
          throw new Error(`Unknown preset type: ${content.presetType}`);
        }

        // Get today's date in ISO format for some APIs that need it
        const today = new Date();
        const dateParam = format(today, 'yyyy-MM-dd');

        // Construct the API URL with any needed parameters
        let apiUrl = endpoint;
        if (content.presetType === "halacha-yomit") {
          apiUrl = `${endpoint}?date=${dateParam}`;
        } else if (content.presetType === "hayom-yom") {
          // For hayom yom from Chabad.org, we don't need any date parameters
          apiUrl = endpoint;
        }

        // Get the current auth token for the request
        const { data: sessionData } = await supabase.auth.getSession();
        const authToken = sessionData.session?.access_token || '';

        const response = await fetch(apiUrl, {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        // Check if the response is JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Invalid response format: Expected JSON but received ' + contentType);
        }

        const data = await response.json();
        setApiData(sanitizeData(data));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch API data");
        console.error("Error fetching preset API data:", err);
        setApiData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPresetData();

    // Set up refresh interval if specified
    if (content.refreshInterval && content.refreshInterval > 0) {
      const interval = setInterval(
        fetchPresetData, 
        content.refreshInterval * 60 * 1000 // Convert minutes to milliseconds
      );
      return () => clearInterval(interval);
    }
  }, [content.presetType, content.refreshInterval]);

  // Calculate text size class based on settings and homepage status
  const getTextSizeClass = () => {
    // For homepage, use even larger sizes
    if (isHomePage) {
      switch (content.textSize) {
        case 'large': return 'text-9xl';
        case 'x-large': return 'text-[11rem]';
        case 'xx-large': return 'text-[12rem]';
        default: return 'text-8xl';
      }
    }

    // Normal page view
    switch (content.textSize) {
      case 'large': return 'text-8xl';
      case 'x-large': return 'text-9xl';
      case 'xx-large': return 'text-[10rem]';
      default: return 'text-7xl';
    }
  };

  // Format the API data for display
  const renderApiContent = () => {
    if (!apiData) return null;

    if (content.presetType === "halacha-yomit") {
      // Halacha Yomit format
      return (
        <div className={`prose max-w-none ${getTextSizeClass()} leading-relaxed font-bold`}>
          <div className="mb-6">
            <h3 className="font-bold text-center mb-3 text-8xl text-shadow-lg">{apiData.title}</h3>
            <div className="text-center mb-4 text-gray-600 text-7xl text-shadow-md">
              {apiData.hebrewDate}
            </div>
            {apiData.content && apiData.content.map((section: any, index: number) => (
              <div key={index} className="mb-6 p-6 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm">
                <h4 className="font-bold mb-3 text-8xl text-shadow-lg">{section.title}</h4>
                <div className="text-right text-shadow-md" dir="rtl">
                  {section.text.split('\n').map((paragraph: string, pIndex: number) => (
                    <p key={pIndex} className="mb-3">{paragraph}</p>
                  ))}
                </div>
                {section.source && (
                  <div className="text-right mt-3 text-gray-600 text-7xl text-shadow-sm">
                    מקור: {section.source}
                  </div>
                )}
              </div>
            ))}
            <div className="text-right mt-4 text-gray-600 text-7xl text-shadow-sm">
              <a 
                href={apiData.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                מקור: {apiData.source}
              </a>
            </div>
          </div>
        </div>
      );
    } else if (content.presetType === "hayom-yom") {
      // Hayom Yom format
      return (
        <div className={`prose max-w-none ${getTextSizeClass()} leading-relaxed font-bold`}>
          <div className="mb-6">
            {!isHomePage && (
              <>
                <h3 className="font-bold text-center mb-3 text-8xl text-shadow-lg">{apiData.title}</h3>
                <div className="text-center mb-4 text-gray-600 text-7xl text-shadow-md">
                  {apiData.hebrewDate}
                </div>
              </>
            )}
            {apiData.shiurim && apiData.shiurim.length > 0 && (
              <div className="mb-6 p-6 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm">
                <h4 className="font-bold mb-3 text-8xl text-shadow-lg">שיעורים:</h4>
                <ul className="list-none p-0 m-0 text-shadow-md">
                  {apiData.shiurim.map((shiur: string, index: number) => (
                    <li key={index} className="mb-2">{shiur}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-6 p-6 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm text-right text-shadow-md" dir="rtl">
              {typeof apiData.content === 'string' 
                ? <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(apiData.content) }} /> 
                : apiData.content}
            </div>
            <div className="text-right mt-4 text-gray-600 text-7xl text-shadow-sm">
              <a 
                href={apiData.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                מקור: {apiData.source}
              </a>
            </div>
          </div>
        </div>
      );
    }

    // Generic JSON view for unknown preset types
    return (
      <div className="whitespace-pre-wrap p-6 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm text-8xl font-bold text-shadow-md">
        {JSON.stringify(apiData, null, 2)}
      </div>
    );
  };

  // Title for the content
  const displayTitle = content.customTitle || API_TITLES[content.presetType] || content.title || "תוכן API";

  return (
    <Card className="h-full overflow-y-auto bg-white/90 backdrop-blur-sm">
      <CardContent className={`p-8 ${isHomePage ? 'text-larger' : ''}`}>
        {!isHomePage && content.showTitle !== false && (
          <h2 className="text-7xl font-bold text-center mb-4 text-shadow-lg">{displayTitle}</h2>
        )}
        {!isHomePage && content.showDate !== false && (
          <div className="text-center text-gray-600 mb-6 text-7xl text-shadow-md">
            {format(new Date(), "dd MMMM yyyy", { locale: he })}
          </div>
        )}
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-800 p-6 rounded-lg">
            <h3 className="font-bold mb-2 text-7xl text-shadow-md">{isHomePage ? 'שגיאה' : 'שגיאה בטעינת הנתונים'}</h3>
            <p className={`${isHomePage ? 'text-8xl' : 'text-7xl'} text-shadow-sm`}>{error}</p>
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur-sm rounded-lg">
            {renderApiContent()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PresetApiPage; 